import { createContext, useContext, useMemo, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import {
  IAccountListResponse,
  IBidResponse,
  ICommunitiesResponse,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAccounts, useCommunities } from "@mibuildweb/hooks";
import { schema } from "./schema/schema";
import { CreateBidPayload } from "./CreateBids.types";
import { STEP } from "./constants";
import { useCreateBidViewModel } from "./useCreateBidViewModel";

const CreateBidContext = createContext<CreateBidContextType | undefined>(
  undefined
);

export function useCreateBidContext() {
  const context = useContext(CreateBidContext);
  if (!context)
    throw new Error(
      "useCreateBidContext must be used within CreateBidProvider"
    );
  return context;
}

export function CreateBidProvider({ children }: CreateBidProviderProps) {
  const [activeStep, setActiveStep] = useState(STEP.DETAILS);
  const [isConfigStepValid, setIsConfigStepValid] = useState(false);

  const form = useForm<CreateBidPayload>({
    mode: "onChange",
    defaultValues: {
      id: createUUID(),
      name: "",
      communityId: "",
      builderId: "",
      additionalFeatures: "",
      dueDate: "",
      pipeBrand: "",
      documents: [],
      planAreas: [],
    },
    resolver: zodResolver(schema),
  });

  const {
    isLoading: isBidLoading,
    bidDetails,
    fetchBidDetails,
    handleCreateBid,
  } = useCreateBidViewModel({ reset: form.reset });
  const {
    isLoading: isCommunitiesLoading,
    communities,
    getCommunities,
  } = useCommunities();
  const { isLoading: isAccountsLoading, accounts, getAccounts } = useAccounts();

  const value = useMemo(
    () => ({
      form,
      activeStep,
      bidDetails,
      fetchBidDetails,
      setActiveStep,
      handleCreateBid,
      communities,
      getCommunities,
      accounts,
      getAccounts,
      isLoading: isBidLoading || isCommunitiesLoading || isAccountsLoading,
      isConfigStepValid,
      setIsConfigStepValid,
    }),
    [form.formState, activeStep, bidDetails, isConfigStepValid]
  );

  return (
    <CreateBidContext.Provider value={value}>
      {children}
    </CreateBidContext.Provider>
  );
}

interface CreateBidProviderProps {
  children: React.ReactNode;
}

interface CreateBidContextType {
  isLoading: boolean;
  form: UseFormReturn<CreateBidPayload>;
  activeStep: number;
  bidDetails: IBidResponse | null;
  fetchBidDetails: (bidId: string) => void;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  handleCreateBid: (data: CreateBidPayload) => Promise<void>;
  communities: ICommunitiesResponse[];
  getCommunities: () => void;
  accounts: IAccountListResponse[];
  getAccounts: () => void;
  isConfigStepValid: boolean;
  setIsConfigStepValid: React.Dispatch<React.SetStateAction<boolean>>;
}
